import { localizeLabelCategory, localizeLabelValueName, updateLabelValue } from '@tableau/catalog-ui-temp';
import { assertUnreachable, restApiService } from '@tableau/vizportalclient-common-components';
import { call, put } from 'redux-saga/effects';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { SetCertificationOrTriggerWarningAction } from '~src/catalog/actions/labels/SetCertificationOrTriggerWarningAction';
import { getCertificationOrTriggerWarningActionDialogOptions } from '~src/catalog/components/labelValues/CertificationOrTriggerWarningLabelValueDialog';
import { defaultLabelValueInitialState, LabelValueDialogBodyProps, LabelValueDialogState } from '~src/catalog/components/labelValues/LabelValueDialogHelper';
import { LabelValueContentItem } from '~src/catalog/restApi/labels/LabelValueTypes';
import { getItemsWithCorrectLuids } from '~src/Collections/sagas/CollectionSagas';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../../codegen/Localize';


export function* handleSetCertificationOrTriggerWarning(action: SetCertificationOrTriggerWarningAction) {
  try {

    yield call(openCertificationOrTriggerWarningDialog, action);

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function openCertificationOrTriggerWarningDialog(action: SetCertificationOrTriggerWarningAction): PromiseLike<unknown> {
  const itemWithCorrectLuid = getItemsWithCorrectLuids([action.item])[0];
  const { id: contentId, name: value, contentType: contentType} = itemWithCorrectLuid;
  const labelValueName = action.item.value;
  const category = action.item.category;
  const description = action.item.labelDescription;
  const visibility = action.item.visiblity;
  const builtIn = action.item.builtIn;
  if (contentType !== 'labelValue') {
    throw new Error(` user cannot change description on ${action.item.contentType}.`);
  }

  const contentItem: LabelValueContentItem = { contentId, labelValueName, category, description, contentType, visibility, builtIn };

  return openCertificationOrTriggerWarningDialogCatalog({ contentItem, action });
}

export function openCertificationOrTriggerWarningDialogCatalog({ contentItem, action, onSubmit }: {
  readonly contentItem: LabelValueContentItem;
  readonly action?: SetCertificationOrTriggerWarningAction;
  readonly onSubmit?: () => void;
}): Promise<unknown> {


  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());

  const {makeJsonRestApiRequest} = restApiService({siteLuid});
  const doAction: (formData: LabelValueDialogState) => Promise<unknown> = ({ labelValueName, category, description, elevatedDefault }) => {
    const labelValue = {name: labelValueName, category, description, elevatedDefault};
    return updateLabelValue(
      {makeJsonRestApiRequest,
      labelValue, name:labelValueName},
    ).then(async (_: unknown) => {
      ToasterService.instance.pop({
        type: ToastType.Info,
        text: Messages.labelValueUpdateSuccess({
          LABEL_CATEGORY: localizeLabelCategory(category),
          LABEL_VALUE_NAME: localizeLabelValueName(labelValueName),
         }),
      });
      if (onSubmit) {
        ToasterService.instance.markToastsAsDoNotDismissOnLocationChange();
        onSubmit();
      }
    }).catch((_: unknown) => ToasterService.instance.pop({
      type: ToastType.Error,
      text: Messages.labeValuelUpdateError({
        LABEL_CATEGORY: localizeLabelCategory(category),
        LABEL_VALUE_NAME: localizeLabelValueName(labelValueName),
      })
    }));
  };

  const initialBodyProps: LabelValueDialogBodyProps = { contentItem };
  const modalInstance = openReactActionDialog(getCertificationOrTriggerWarningActionDialogOptions({
    initialBodyProps,
    initialFormData: defaultLabelValueInitialState(contentItem.category, contentItem.labelValueName),
    doAction,
    dialogType: convertLabelValueCategory(contentItem.category),
  }));

  return modalInstance.result;
}

function convertLabelValueCategory(category: string) {
  switch (category) {
    case 'WARNING':
      return 'warning';
    case 'SENSITIVITY':
      return 'sensitivity';
    case 'CERTIFICATION':
      return 'certification';
    default:
      assertUnreachable;
      return 'warning';
  }
}
